import { styled } from "goober";
import { useEffect, useRef, useState } from "react";
import { zIndexes } from "../lib/zIndexes";
import { AnchoredPopover } from "./AnchoredPopover";
import { InfoIcon } from "./InfoIcon";
import { Text } from "./Text";
import {
  getLastTooltipCloseTime,
  setLastTooltipCloseTime,
  TOOLTIP_DELAY,
  WARM_PERIOD,
} from "./Tooltip";

const CLOSE_DELAY = 150;
const MAX_WIDTH = 320;

const Popover = styled(AnchoredPopover)`
  background: var(--tooltip-bg);
  color: var(--tooltip-fg);
  box-shadow: var(--elevation-low);
  z-index: ${zIndexes.tooltip};
  width: max-content;
  max-width: ${MAX_WIDTH}px;
  text-align: left;
  font-size: 10px;
`;

type Props = {
  description: string;
  iconColor?: string;
  iconVisible?: boolean;
  idleOpacity?: number;
  anchorName?: string;
  anchorRef?: React.RefObject<HTMLElement | null>;
};

export function ReadmeDescriptionTooltip({
  description,
  iconColor = "currentColor",
  iconVisible = true,
  idleOpacity = 1,
  anchorName,
  anchorRef,
}: Props) {
  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [position, setPosition] = useState<React.CSSProperties>({});

  const localRef = useRef<HTMLSpanElement>(null);

  const openTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelOpen = () => {
    if (openTimerRef.current) {
      clearTimeout(openTimerRef.current);
      openTimerRef.current = null;
    }
  };

  const cancelClose = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const computePosition = (): React.CSSProperties => {
    const el = (anchorRef ?? localRef).current;
    const styles: Record<string, string> = {};
    if (!el) return styles;
    const rect = el.getBoundingClientRect();
    if (rect.top < 120) {
      styles.top = "anchor(bottom)";
      styles.marginTop = "6px";
    } else {
      styles.bottom = "anchor(top)";
      styles.marginBottom = "6px";
    }
    if (anchorName) {
      // External anchor (a step card): align to its left edge, flipping to the
      // right edge near the viewport edge.
      if (window.innerWidth - rect.left < MAX_WIDTH + 20) {
        styles.right = "anchor(right)";
      } else {
        styles.left = "anchor(left)";
      }
    } else {
      // Self-anchored to the small icon: center over it, clamping to an edge
      // when centering would overflow the viewport.
      const center = rect.left + rect.width / 2;
      if (center + MAX_WIDTH / 2 + 8 > window.innerWidth) {
        styles.right = "anchor(right)";
      } else if (center - MAX_WIDTH / 2 - 8 < 0) {
        styles.left = "anchor(left)";
      } else {
        styles.left = "anchor(center)";
        styles.transform = "translateX(-50%)";
      }
    }
    return styles as React.CSSProperties;
  };

  const show = () => {
    setPosition(computePosition());
    setOpen(true);
  };

  const scheduleClose = () => {
    cancelClose();
    closeTimerRef.current = setTimeout(() => {
      setOpen(false);
      setLastTooltipCloseTime(Date.now());
    }, CLOSE_DELAY);
  };

  const handleIconEnter = () => {
    setHovered(true);
    cancelClose();
    cancelOpen();
    if (Date.now() - getLastTooltipCloseTime() < WARM_PERIOD) {
      show();
      return;
    }
    openTimerRef.current = setTimeout(show, TOOLTIP_DELAY);
  };

  const handleIconLeave = () => {
    setHovered(false);
    cancelOpen();
    if (open) scheduleClose();
  };

  useEffect(
    () => () => {
      if (openTimerRef.current) clearTimeout(openTimerRef.current);
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    },
    []
  );

  const restOpacity = hovered || open ? 1 : iconVisible ? idleOpacity : 0;

  return (
    <Popover
      isOpen={open}
      target={anchorName}
      style={position}
      trigger={({ style: anchorStyle }) => (
        <InfoIcon
          ref={anchorRef ? undefined : localRef}
          color={iconColor}
          idleOpacity={restOpacity}
          style={anchorStyle}
          onPointerDown={(e) => e.stopPropagation()}
          onMouseEnter={handleIconEnter}
          onMouseLeave={handleIconLeave}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        />
      )}
    >
      <div
        style={{ padding: "6px 8px" }}
        onMouseEnter={cancelClose}
        onMouseLeave={scheduleClose}
        onPointerDown={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        <Text variant="text-small-md">{description}</Text>
      </div>
    </Popover>
  );
}
